import { useEffect, useState } from 'react';
import moment from 'moment';

import { retornaData, retornaDiasAtrasoVencimento } from '../../../../utils/retornaDataFormatada';
import { formatarNumero, formatarNumeroDecimal } from '../../../../utils/masks';
import { calculaDataVencimento } from '../../../../utils/financeiro';
import { checarCnhHandler } from '../../../../api/cnh';
import { formatCnhFeedback } from '../../../../utils/cnhFeedback';

import * as DataListAPI from '../../../../api/dataList';
import * as FinanceiroAPI from '../../../../api/financeiro';
import * as PessoaAPI from '../../../../api/pessoa';
import * as ProdutoAPI from '../../../../api/produto';
import * as TabelaAPI from '../../../../api/tabela';

import {
    calcularValorParcela,
    calcularValorAberto,
    aplicarDescontoPercentual,
    aplicarDescontoValor,
    aplicarAcrescimoPercentual,
    aplicarAcrescimoValor,
} from '../helpers/calcLancamento';

export function useLancamentoModule({
    token,
    tipo,
    btnText,
    empresaIds,
    parametro,
    setFlashMessage,
    dados,
    setDados,
    pessoa,
    setPessoa,
    centroCusto,
    setCentroCusto,
    subCentroCusto,
    setSubCentroCusto,
    lancamentoBaixa,
    setLancamentoBaixa,
    mapaAbastecimento,
    setMapaAbastecimento,
    optionsCentroCustos,
    handleSubmit,
}) {
    const [optionsPrazos, setPrazo] = useState([]);
    const [optionsConta, setConta] = useState([]);
    const [optionsContaDestino, setContaDestino] = useState([]);
    const [contasDisponiveis, setContasDisponiveis] = useState([]);

    const [divBaixaLancamento, setDivBaixaLancamento] = useState(false);
    const [divMapaAbastecimento, setDivMapaAbastecimento] = useState(false);
    const [divCalculaJuros, setDivCalculaJuros] = useState(false);
    const [readOnlyCalculaJuros, setReadOnlyCalculaJuros] = useState(false);

    const [isSubmitting, setIsSubmitting] = useState(false);

    const [optionsParceiro, setOptionsParceiro] = useState([]);
    const [optionsCC, setOptionsCC] = useState(optionsCentroCustos || []);
    const [optionsMotorista, setMotorista] = useState([]);
    const [optionsVTR, setVTR] = useState([]);

    const consultaDatalist = async (params) => {
        try {
            return await DataListAPI.dataList(params);
        } catch (error) {
            console.error('Erro ao consultar DataList:', error);
            throw error;
        }
    };

    const consultaPrazo = async (tipoConsulta, formaId, nivel, prazoId, empresaIdConsulta = dados.empresaId, prazoMedio) => {
        const result = await FinanceiroAPI.prazo(tipoConsulta, formaId, nivel, prazoId, empresaIdConsulta, prazoMedio);
        setPrazo(result || []);
        return result;
    };

    const consultaParceiro = async (pessoaIdConsulta, empresaIdConsulta) => {
        return await PessoaAPI.financeiro(pessoaIdConsulta, empresaIdConsulta);
    };

    const consultaColaborador = async (empresaIdConsulta, filtro, condicao) => {
        const result = await PessoaAPI.colaborador(empresaIdConsulta, filtro, condicao);
        setMotorista(result);

        setMapaAbastecimento((prev) => ({
            ...prev,
            mapa: true,
            data: dados.dataLancamento,
            parceiroId: dados.pessoaId,
            valor: dados.valorOriginal,
            empresaId: dados.empresaId,
            pessoaId: result[0]?.id,
        }));

        return result;
    };

    const validarCnhMapa = async ({ produtoId, pessoaId, data }) => {
        if (!produtoId || !pessoaId || !data) return;

        try {
            const validaCNH = await checarCnhHandler({
                produtoId,
                pessoaId,
                data,
            });

            const { text, severity } = formatCnhFeedback(validaCNH);

            if (!validaCNH.ok || validaCNH?.validade?.status === 'expiring') {
                setFlashMessage(text, severity, 5);
            }
        } catch (error) {
            console.error(error);
            setFlashMessage('Falha ao validar CNH do condutor.', 'danger', 5);
        }
    };

    const consultaVTR = async (empresaIdConsulta, ativo, filtro, condicao) => {
        if (!empresaIdConsulta) return null;

        try {
            let kmAnterior = 0;
            let produtoId = null;
            let motoristaId = null;

            const motorista = await consultaColaborador(empresaIdConsulta, null, 'motorista');
            const result = await ProdutoAPI.bem(empresaIdConsulta, ativo, filtro, condicao);

            if (Array.isArray(motorista) && motorista.length > 0 && motorista[0]?.id) {
                motoristaId = motorista[0].id;
            }

            if (Array.isArray(result) && result.length > 0 && result[0]?.id) {
                kmAnterior = await ProdutoAPI.ultimoKm(result[0].id);
                produtoId = result[0].id;
            }

            setVTR(result);

            setMapaAbastecimento((prev) => ({
                ...prev,
                mapa: true,
                data: dados.dataLancamento,
                parceiroId: dados.pessoaId,
                valor: dados.valorOriginal,
                empresaId: dados.empresaId,
                produtoId,
                pessoaId: motoristaId,
                kmAnterior,
            }));
            const novoMapa = {
                ...mapaAbastecimento,
                mapa: true,
                data: dados.dataLancamento,
                parceiroId: dados.pessoaId,
                valor: dados.valorOriginal,
                empresaId: dados.empresaId,
                produtoId,
                pessoaId: motoristaId,
                kmAnterior,
            };

            setMapaAbastecimento((prev) => ({
                ...prev,
                ...novoMapa,
            }));

            await validarCnhMapa({
                produtoId,
                pessoaId: motoristaId,
                data: novoMapa.data,
            });
        } catch (error) {
            console.error('Erro na consulta de VTR:', error);
        }
    };

    const retornaCC = async (empresaIdConsulta, centroCustoId) => {
        if (!empresaIdConsulta || !centroCustoId) return;

        try {
            const result = await TabelaAPI.tabelaById(empresaIdConsulta, 'centroCusto', centroCustoId);

            if (result?.combustivel === true) {
                await consultaVTR(empresaIdConsulta, true, null, 'Móvel');
                if (btnText === 'Cadastrar') setDivMapaAbastecimento(true);
            } else {
                setDivMapaAbastecimento(false);
                setMapaAbastecimento({
                    mapa: false,
                    data: dados.dataLancamento,
                    empresaId: empresaIdConsulta,
                    produtoId: null,
                    pessoaId: null,
                    parceiroId: dados.pessoaId,
                    kmAnterior: null,
                    km: null,
                    preco: null,
                    litros: null,
                    valor: null,
                });
            }

            return result;
        } catch (error) {
            console.error('Erro ao buscar centro de custo:', error);
            throw error;
        }
    };

    const retornaForma = async (empresaIdConsulta, formaId) => {
        if (!empresaIdConsulta || !formaId) return;

        try {
            const forma = await TabelaAPI.tabelaById(empresaIdConsulta, 'pagamentoForma', formaId);
            await consultaPrazo(tipo, forma?.id, forma?.nivelVenda, '', forma?.empresaId, '');

            if (forma?.baixaLancamento === true) {
                setDivBaixaLancamento(true);
                setLancamentoBaixa({
                    baixaLancamento: true,
                    baixa: lancamentoBaixa.baixa ? lancamentoBaixa.baixa : dados.valorAberto,
                    dataBaixa: lancamentoBaixa.dataBaixa ? lancamentoBaixa.dataBaixa : dados.dataLancamento,
                    btnText,
                });
            } else {
                setDivBaixaLancamento(false);
                setLancamentoBaixa({
                    baixaLancamento: false,
                    baixa: null,
                    dataBaixa: null,
                    btnText,
                });
            }
        } catch (error) {
            console.error('Erro ao buscar forma:', error);
            throw error;
        }
    };

    const buscaConta = async (empresaIdsConsulta, contaOrContaDestino) => {
        if (!empresaIdsConsulta || empresaIdsConsulta.length === 0) return;

        try {
            const result = await FinanceiroAPI.buscaContaByEmpresaIds({ empresaIds: empresaIdsConsulta });
            if (contaOrContaDestino === 'conta') setConta(result);
            else if (contaOrContaDestino === 'destino') setContaDestino(result);
        } catch (error) {
            console.error('Erro ao buscar conta:', error);
        }
    };

    const atualizaLancamentoBaixa = (baixa, dataBaixa) => {
        setLancamentoBaixa((prev) => ({
            ...prev,
            baixa,
            dataBaixa,
        }));
    };

    const atualizaLancamento = () => {
        let valorAtual = dados.valorAberto;

        if (dados.calculaJuros === true) {
            setReadOnlyCalculaJuros(false);

            const diasAtraso = Number(retornaDiasAtrasoVencimento(null, dados.dataVencimento));

            if (diasAtraso <= 0) {
                setDados((prev) => ({
                    ...prev,
                    diasAtraso,
                    juros: 0,
                    multa: 0,
                    valorAberto: prev.valorAberto,
                }));
                return;
            }

            const multa = (Number(dados.valorAberto || 0) * Number(dados.multaPercentual || 0)) / 100;
            const juros = Number(dados.valorAberto || 0) * (Number(dados.jurosPercentual || 0) / 100 / 30) * diasAtraso;

            const multaFmt = parseFloat(multa).toFixed(2);
            const jurosFmt = parseFloat(juros).toFixed(2);

            const valAberto = Number(dados.valorAberto || 0);
            const valMulta = Number(multaFmt || 0);
            const valJuros = Number(jurosFmt || 0);

            valorAtual = parseFloat(valAberto - valMulta - valJuros).toFixed(2);

            setDados((prev) => ({
                ...prev,
                diasAtraso,
                multa: multaFmt,
                juros: jurosFmt,
                valorAberto: valorAtual,
            }));
        } else {
            setReadOnlyCalculaJuros(true);

            const valAberto = Number(dados.valorAberto || 0);
            const valMulta = Number(dados.multa || 0);
            const valJuros = Number(dados.juros || 0);

            valorAtual = parseFloat(valAberto - valMulta - valJuros).toFixed(2);

            setDados((prev) => ({
                ...prev,
                multa: 0,
                juros: 0,
                valorAberto: valorAtual,
            }));
        }
    };

    useEffect(() => {
        buscaConta(empresaIds, 'conta');
        buscaConta(empresaIds, 'destino');
        consultaPrazo(tipo, '', '', '', dados.empresaId, '');
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dados.empresaId, tipo]);

    useEffect(() => {
        atualizaLancamento();
        setDivCalculaJuros(dados.calculaJuros);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dados.calculaJuros]);

    useEffect(() => {
        if (btnText === 'Baixar') return;
        const { formaId, centroCustoId, empresaId } = dados;
        if (empresaId && formaId) retornaForma(empresaId, formaId);
        if (empresaId && centroCustoId) retornaCC(empresaId, centroCustoId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dados.formaId, dados.centroCustoId, dados.empresaId, btnText]);

    useEffect(() => {
        setDados((prev) => ({ ...prev, lancamentoBaixa }));
    }, [lancamentoBaixa, setDados]);

    useEffect(() => {
        setDados((prev) => ({ ...prev, mapaAbastecimento }));
    }, [mapaAbastecimento, setDados]);

    useEffect(() => {
        if (dados.empresaId && empresaIds.length > 1 && parametro.multiEmpresaContas === false) {
            buscaConta([dados.empresaId], 'conta');
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dados.empresaId, parametro.multiEmpresaContas]);

    useEffect(() => {
        const fetchData = async () => {
            // 🔴 VALIDAÇÃO DOS PARÂMETROS
            if (
                !parametro?.pessoaId ||
                !parametro?.centroCustoTransferencia ||
                !parametro?.transferenciaForma ||
                !parametro?.transferenciaPrazo
            ) {
                setFlashMessage(
                    'Parâmetros de transferência não configurados. Verifique pessoa, centro de custo, forma e prazo.',
                    'warning'
                );
                return;
            }

            // contas disponíveis
            if (dados.contaId) {
                const novasOpcoesContaDestino = optionsContaDestino
                    .filter((opcao) => opcao.id !== parseInt(dados.contaId))
                    .sort((a, b) => a.descricao.localeCompare(b.descricao));
                setContasDisponiveis(novasOpcoesContaDestino);
            }

            // parceiro
            const resultPessoa = await consultaParceiro(parametro.pessoaId, dados.empresaId);
            setPessoa(resultPessoa);
            setOptionsParceiro(resultPessoa);

            // centro de custo
            const resultCC = await retornaCC(dados.empresaId, parametro.centroCustoTransferencia);
            setCentroCusto((prev) => ({ ...prev, descricao: resultCC?.descricao || '' }));

            // dados padrão
            setDados((prev) => ({
                ...prev,
                pessoaId: parametro.pessoaId,
                centroCustoId: parametro.centroCustoTransferencia,
                formaId: parametro.transferenciaForma,
                prazoId: parametro.transferenciaPrazo,
                historico: 'TRF Contas',
            }));
        };

        if (dados.transferencia === true) fetchData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dados.transferencia, dados.contaId]);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const vtr = await TabelaAPI.tabelaById(dados.empresaId, 'Produto', mapaAbastecimento.produtoId);
                const km = await ProdutoAPI.ultimoKm(vtr.id);

                setMapaAbastecimento((prev) => ({
                    ...prev,
                    mapa: true,
                    data: dados.dataLancamento,
                    parceiroId: dados.pessoaId,
                    valor: dados.valorOriginal,
                    kmAnterior: km,
                }));
            } catch (error) {
                console.error('Erro ao buscar dados:', error);
            }
        };

        if (mapaAbastecimento.produtoId) {
            fetchData();
        }

        if (mapaAbastecimento.produtoId && mapaAbastecimento.pessoaId && mapaAbastecimento.data) {
            validarCnhMapa({
                produtoId: mapaAbastecimento.produtoId,
                pessoaId: mapaAbastecimento.pessoaId,
                data: mapaAbastecimento.data,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mapaAbastecimento.produtoId, mapaAbastecimento.pessoaId, mapaAbastecimento.data, dados.dataLancamento]);

    const handleChange = async (e) => {
        const { name, value, checked, type } = e.target;

        const camposBooleanos = [
            'ativo',
            'provisao',
            'fixaDiaVencimento',
            'transferencia',
            'tabelasComuns',
            'msgCoresDefault',
            'calculaJuros',
        ];
        const camposMonetarios = [
            'valorOriginal',
            'valor',
            'descontoPercentual',
            'descontoValor',
            'acrescimoPercentual',
            'acrescimoValor',
            'baixa',
            'valorAberto',
            'multa',
            'multaPercentual',
            'juros',
            'jurosPercentual',
        ];

        if (camposMonetarios.includes(name)) {
            if (name === 'valorOriginal') {
                const valorFormatado = formatarNumeroDecimal(value);
                const valorParcela = calcularValorParcela(valorFormatado, dados.parcela).toFixed(2);
                const valorAberto = calcularValorAberto({
                    valorOriginal: valorFormatado,
                    descontoValor: dados.descontoValor,
                    acrescimoValor: dados.acrescimoValor,
                    parcela: dados.parcela,
                }).toFixed(2);

                setDados({ ...dados, [name]: valorFormatado, valorAberto, valor: valorParcela });
                setMapaAbastecimento((prev) => ({ ...prev, valor: value }));
            } else if (name === 'descontoPercentual') {
                const valorFormatado = formatarNumeroDecimal(value);
                const { descontoValor, valorBaixa } = aplicarDescontoPercentual({
                    valor: dados.valor,
                    acrescimoValor: dados.acrescimoValor,
                    descontoPercentual: valorFormatado,
                });

                setLancamentoBaixa((prev) => ({ ...prev, baixa: valorBaixa.toFixed(2) }));
                setDados({
                    ...dados,
                    [name]: valorFormatado,
                    descontoValor: descontoValor.toFixed(2),
                    baixa: valorBaixa.toFixed(2),
                    valorAberto: valorBaixa.toFixed(2),
                });
            } else if (name === 'descontoValor') {
                const valorFormatado = formatarNumeroDecimal(value);
                const { descontoPercentual, valorBaixa } = aplicarDescontoValor({
                    valor: dados.valor,
                    acrescimoValor: dados.acrescimoValor,
                    descontoValor: valorFormatado,
                });

                setLancamentoBaixa((prev) => ({ ...prev, baixa: valorBaixa.toFixed(2) }));
                setDados({
                    ...dados,
                    [name]: valorFormatado,
                    descontoPercentual: descontoPercentual.toFixed(2),
                    baixa: valorBaixa.toFixed(2),
                    valorAberto: valorBaixa.toFixed(2),
                });
            } else if (name === 'acrescimoPercentual') {
                const valorFormatado = formatarNumeroDecimal(value);
                const { acrescimoValor, valorBaixa } = aplicarAcrescimoPercentual({
                    valor: dados.valor,
                    descontoValor: dados.descontoValor,
                    acrescimoPercentual: valorFormatado,
                });

                setLancamentoBaixa((prev) => ({ ...prev, baixa: valorBaixa.toFixed(2) }));
                setDados({
                    ...dados,
                    [name]: valorFormatado,
                    acrescimoValor: acrescimoValor.toFixed(2),
                    baixa: valorBaixa.toFixed(2),
                    valorAberto: valorBaixa.toFixed(2),
                });
            } else if (name === 'acrescimoValor') {
                const valorFormatado = formatarNumeroDecimal(value);
                const { acrescimoPercentual, valorBaixa } = aplicarAcrescimoValor({
                    valor: dados.valor,
                    descontoValor: dados.descontoValor,
                    acrescimoValor: valorFormatado,
                });

                setLancamentoBaixa((prev) => ({ ...prev, baixa: valorBaixa.toFixed(2) }));
                setDados({
                    ...dados,
                    [name]: valorFormatado,
                    acrescimoPercentual: acrescimoPercentual.toFixed(2),
                    baixa: valorBaixa.toFixed(2),
                    valorAberto: valorBaixa.toFixed(2),
                });
            } else if (name === 'valor') {
                const valorFormatado = formatarNumeroDecimal(value);
                const valorOriginal = (Number(valorFormatado || 0) * Number(dados.parcela || 1)).toFixed(2);
                setDados({ ...dados, [name]: valorFormatado, valorOriginal });
            } else {
                setDados({ ...dados, [name]: formatarNumeroDecimal(value) });
            }
        } else if (camposBooleanos.includes(name)) {
            setDados({ ...dados, [name]: checked });
        } else if (name === 'parcela') {
            const valorParcela = calcularValorParcela(dados.valorOriginal, value).toFixed(2);
            setDados({ ...dados, [name]: value, valor: valorParcela, valorAberto: valorParcela });
        } else if (name.startsWith('centroCusto.')) {
            setCentroCusto({ ...centroCusto, [name.split('.')[1]]: value });

            const result = await consultaDatalist({
                empresaId: dados.empresaId,
                tabela: 'centroCusto',
                condicao: tipo,
                filtro: value,
            });

            setOptionsCC(result);
            const selectedId = document.querySelector(`datalist option[value="${value}"]`)?.dataset.id;
            setDados((prev) => ({ ...prev, centroCustoId: selectedId ?? null }));
        } else if (name.startsWith('subCentroCusto.')) {
            setSubCentroCusto({ ...subCentroCusto, [name.split('.')[1]]: value });

            const result = await consultaDatalist({
                empresaId: dados.empresaId,
                tabela: 'centroCusto',
                condicao: tipo,
                filtro: value,
            });

            setOptionsCC(result);
            const selectedId = document.querySelector(`datalist option[value="${value}"]`)?.dataset.id;
            setDados((prev) => ({ ...prev, subCentroCustoId: selectedId ?? null }));
        } else if (name === 'dataLancamento') {
            setDados({ ...dados, [name]: value });
            const condicoes = await calculaDataVencimento(token, value, dados.formaId, dados.prazoId);

            setDados((prev) => ({
                ...prev,
                parcela: condicoes.parcela,
                dataVencimento: condicoes.vencimento ?? retornaData(moment()),
            }));
        } else if (name.startsWith('Pessoa.')) {
            setPessoa({ ...pessoa, [name.split('.')[1]]: value });

            const result = await consultaDatalist({
                empresaId: dados.empresaId,
                tabela: 'Pessoa',
                id: 'id',
                descricao: 'descricao',
                condicao: tipo,
                filtro: value,
            });

            setOptionsParceiro(result);
            const selectedId = document.querySelector(`datalist option[value="${value}"]`)?.dataset.id;

            if (selectedId) {
                const resultado = await consultaParceiro(selectedId, dados.empresaId);

                await consultaPrazo(
                    tipo,
                    tipo === 'Pagar' ? resultado?.formaFornecedorId : resultado?.formaClienteId,
                    tipo === 'Pagar' ? resultado?.formaFornecedor?.nivelVenda : resultado?.formaCliente?.formaCliente?.nivelVenda,
                    dados.empresaId,
                    tipo === 'Pagar'
                        ? (resultado?.prazoFornecedor?.prazoMedio ?? null)
                        : (resultado?.prazoCliente?.prazoMedio ?? null)
                );

                const centroCustoSelecionado = await TabelaAPI.tabelaById(
                    dados.empresaId,
                    'centroCusto',
                    tipo === 'Pagar' ? resultado?.centroCustoFornecedorId : resultado?.centroCustoClienteId
                );

                if (centroCustoSelecionado) {
                    setCentroCusto({ ...centroCusto, descricao: centroCustoSelecionado.descricao });
                }

                const condicoes = await calculaDataVencimento(
                    token,
                    dados.dataLancamento,
                    tipo === 'Pagar' ? resultado?.formaFornecedorId : resultado?.formaClienteId,
                    null
                );

                setDados((prev) => ({
                    ...prev,
                    pessoaId: selectedId,
                    centroCustoId: tipo === 'Pagar' ? resultado?.centroCustoFornecedorId : resultado?.centroCustoClienteId,
                    contaId: tipo === 'Pagar' ? resultado?.contaFornecedorId : resultado?.contaClienteId,
                    formaId: tipo === 'Pagar' ? resultado?.formaFornecedorId : resultado?.formaClienteId,
                    prazoId: tipo === 'Pagar' ? resultado?.prazoFornecedorId : resultado?.prazoClienteId,
                    dataLancamento: dados.dataLancamento || retornaData(),
                    dataVencimento: condicoes.vencimento ?? retornaData(moment()),
                    parcela:
                        tipo === 'Pagar' ? (resultado?.prazoFornecedor?.parcela ?? 1) : (resultado?.prazoCliente?.parcela ?? 1),
                    // Adicionando o pixPadrao condicionalmente
                    ...(tipo === 'Pagar' && { codBarraPIX: resultado?.pixPadrao }),
                }));
            } else {
                setDados({
                    ...dados,
                    pessoaId: null,
                    centroCustoId: null,
                    contaId: null,
                    formaId: null,
                    prazoId: null,
                });
                setCentroCusto({ ...centroCusto, descricao: '' });
            }
        } else if (name === 'formaId') {
            setDados({ ...dados, [name]: value });
            await consultaPrazo(tipo, value, '', '', dados.empresaId, null);

            const condicoes = await calculaDataVencimento(token, dados.dataLancamento, value, '');

            setDados((prev) => ({
                ...prev,
                dataVencimento: condicoes.vencimento ?? retornaData(moment()),
                lancamentoBaixa: {
                    baixaLancamento: condicoes.baixaLancamento,
                    baixa: lancamentoBaixa.baixa ? lancamentoBaixa.baixa : dados.valorAberto,
                    dataBaixa: lancamentoBaixa.dataBaixa
                        ? lancamentoBaixa.dataBaixa
                        : moment().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
                },
            }));
        } else if (name === 'prazoId') {
            setDados((prev) => ({ ...prev, [name]: value }));

            const condicoes = await calculaDataVencimento(token, dados.dataLancamento, dados.formaId, value);

            const parcela = Number(condicoes.parcela || 1);
            const valor = calcularValorParcela(dados.valorOriginal, parcela).toFixed(2);
            const valorAberto = calcularValorAberto({
                valorOriginal: dados.valorOriginal,
                descontoValor: dados.descontoValor,
                acrescimoValor: dados.acrescimoValor,
                parcela,
            }).toFixed(2);

            setDados((prev) => ({
                ...prev,
                parcela,
                dataVencimento: condicoes.vencimento ?? retornaData(moment()),
                valor,
                valorAberto,
            }));

            atualizaLancamentoBaixa(valorAberto, dados.dataVencimento);
        } else if (name.startsWith('lancamentoBaixa.')) {
            let newValue = null;

            if (name === 'lancamentoBaixa.baixaLancamento') newValue = checked;
            else if (name === 'lancamentoBaixa.baixa') newValue = formatarNumeroDecimal(value);
            else newValue = value;

            setLancamentoBaixa((prev) => ({
                ...prev,
                [name.split('.')[1]]: newValue,
            }));
        } else if (name.startsWith('mapaAbastecimento.')) {
            const campo = name.split('.')[1];
            const valorCampo =
                campo === 'mapa' ? checked : campo === 'litros' || campo === 'preco' ? formatarNumeroDecimal(value) : value;

            setMapaAbastecimento((prev) => ({
                ...prev,
                [campo]: valorCampo,
                valor: dados?.valorOriginal ?? 0,
            }));
        } else {
            setDados((prev) => ({
                ...prev,
                [name]: type === 'checkbox' ? checked : value,
            }));
        }
    };

    const handleOnBlur = async (e) => {
        const { name, value } = e.target;

        const camposMonetarios = [
            'valorOriginal',
            'valor',
            'descontoPercentual',
            'descontoValor',
            'acrescimoPercentual',
            'acrescimoValor',
            'baixa',
            'valorAberto',
            'multa',
            'multaPercentual',
            'juros',
            'jurosPercentual',
        ];

        if (camposMonetarios.includes(name)) {
            const baixa = dados.valorAberto;
            const dataBaixa = dados.dataVencimento;
            atualizaLancamentoBaixa(baixa, dataBaixa);

            if (btnText === 'Atualizar') atualizaLancamento();
        } else if (name === 'dataLancamento') {
            const condicoes = await calculaDataVencimento(token, value, dados.formaId, dados.prazoId);
            setDados((prev) => ({
                ...prev,
                parcela: condicoes.parcela,
                dataVencimento: condicoes.vencimento ?? retornaData(moment()),
            }));
        } else if (name === 'formaId') {
            const condicoes = await calculaDataVencimento(token, dados.dataLancamento, value, dados.prazoId || null);
            setDados((prev) => ({
                ...prev,
                parcela: condicoes.parcela,
                dataVencimento: condicoes.vencimento ?? retornaData(moment()),
            }));
        } else if (name === 'prazoId') {
            if (value !== 'Selecione uma opção') {
                const condicoes = await calculaDataVencimento(token, dados.dataLancamento, dados.formaId, value);
                setDados((prev) => ({
                    ...prev,
                    parcela: condicoes.parcela,
                }));
            }
        } else if (name.startsWith('mapaAbastecimento.')) {
            const campo = name.split('.')[1];

            if (campo === 'km') {
                if (parseInt(mapaAbastecimento.kmAnterior) > parseInt(value)) {
                    setFlashMessage('Km atual é menor que o km anterior?', 'error');
                }
            } else if (campo === 'preco') {
                const valorAbastecimento = parseFloat(mapaAbastecimento.litros * mapaAbastecimento.preco).toFixed(2);
                const valorLancamento = parseFloat(dados.valorOriginal).toFixed(2);

                if (valorAbastecimento !== valorLancamento) {
                    setFlashMessage(
                        `O valor do lançamento é ${formatarNumero(valorLancamento)} e o valor do abastecimento é ${formatarNumero(valorAbastecimento)}?`,
                        'warning'
                    );
                }
            }
        }
    };

    const handleOnFocus = async () => {};

    const submit = async (e) => {
        e.preventDefault();

        if (isSubmitting) return;
        setIsSubmitting(true);

        try {
            await handleSubmit(dados);
        } finally {
            setIsSubmitting(false);
        }
    };

    return {
        // states auxiliares
        optionsPrazos,
        optionsConta,
        optionsContaDestino,
        contasDisponiveis,
        divBaixaLancamento,
        divMapaAbastecimento,
        divCalculaJuros,
        readOnlyCalculaJuros,
        isSubmitting,
        optionsParceiro,
        optionsCC,
        optionsMotorista,
        optionsVTR,

        // handlers
        handleChange,
        handleOnBlur,
        handleOnFocus,
        submit,
    };
}
